import React, { useRef, useState } from 'react';
import { authFetch, API } from '../api';
import { scanSound } from '../scanSound';
import { MoveView } from './InboundMove';
import PinPad from '../pp2/PinPad';
import { ReadyInbound, TabletStop, TripView, post, detailOf } from './inboundApi';

// An inbound replenish trip on the cart tablet (Fernando 1.11: "always a tablet"; the laptop scans, the cart walks —
// one person alone or two at once). The WMS planned ONE trip when the PIN was given (docs/INBOUND.md): load the cart at
// the inbound station in the order shown (the first stop's box on top), walk the stops one at a time — Done at each
// shelf (the tablet has no scanner: a tap), a shelf for a product that has none, the move to a new shelf when the shelf
// is full, or the box back to the station — then "Back at the station", and the next trip if more is ready.
// Light like the picking screen; EN / ET / RU by the person's language. -- Claude, 2026-11-01
// 2.11 (Fernando): the end of a round is guided — "All replenished, make sure the last box is empty" → "Pick up all the
// boxes and go back to the station" + Done → "Put the boxes back on the shelf" + Start a new round (NO PIN: the
// replenishing session goes on); Pause and Hand over on every screen.

type Lang = 'en' | 'et' | 'ru';
const T = {
  en: {
    trip: (n: number, name: string) => `Trip ${n} · ${name}`, inbound: (w: string) => `Inbound ${w}`, pause: 'Pause',
    loadAt: (s: string) => `Load the cart at ${s}`,
    loadHint: 'Put the boxes on the cart in this order — the first at the bottom, the last on top (it is your first stop). Tap each box as it goes on the cart; boxes you do not tap stay at the station for the next trip.',
    bottom: 'bottom', top: 'top', onCart: 'on the cart ✓', tapIt: 'tap when it is on the cart', stays: 'stays at the station',
    count: (n: number, m: number) => `${n} of ${m} on the cart`, loaded: 'Loaded — go ✓',
    more: (aisle: string, n: number, pcs: number) => `More for ${aisle} at the station: ${n} box${n === 1 ? '' : 'es'} (${pcs} pcs) — take them too if they fit`,
    takeToo: 'take too', taken: 'taken ✓', plusMore: (n: number, m: number, x: number) => `${n} of ${m} planned + ${x} more on the cart`,
    pcs: (n: number) => `${n} pcs`, box: 'Box', stopOf: (i: number, n: number) => `Stop ${i} of ${n}`, done: 'Done — on the shelf ✓',
    noShelf: 'This product has no shelf yet. Choose a free shelf and type it:', shelfFull: 'Shelf full — move to a new shelf',
    moveOn: 'Continue the move to a new shelf', bringBack: 'Bring it back to the station', typeLabel: 'type the shelf label',
    toStock: 'These sizes get this shelf (they were at Ä)', allDone: 'Every box is on its shelf ✓',
    backAt: (s: string) => `Take the empty boxes back to ${s}.`, backBtn: 'Back at the station ✓',
    moreReady: (w: string, n: number) => `Inbound ${w}: ${n} more box${n === 1 ? '' : 'es'} ready`, nextTrip: 'Next trip (PIN)',
    nothingReady: 'Nothing else is ready right now.', ok: 'OK', stops: 'All stops', shelved: 'on the shelf',
    emptyTitle: 'All replenished ✓', emptyHint: 'Make sure the last box is empty — nothing is left in it.', emptyBtn: 'The last box is empty ✓',
    goBack: (s: string) => `Pick up all the boxes and go back to ${s}.`, doneBtn: 'Done ✓',
    shelfBack: 'Put the boxes back on the shelf.', newRound: 'Start a new round',
    newRoundOf: (w: string, n: number) => `Inbound ${w}: ${n} box${n === 1 ? '' : 'es'} ready`,
    endSession: 'End replenishing', handover: 'Hand over', handoverTitle: 'The next person: enter your PIN', cancel: 'Cancel',
    sessionOf: (name: string) => `Replenishing: ${name}`,
  },
  et: {
    trip: (n: number, name: string) => `Käik ${n} · ${name}`, inbound: (w: string) => `Sissetulek ${w}`, pause: 'Paus',
    loadAt: (s: string) => `Lae käru: ${s}`,
    loadHint: 'Pane kastid kärule selles järjekorras — esimene alla, viimane peale (see on sinu esimene peatus). Vajuta igale kastile, kui see on kärul; kastid, millele ei vajuta, jäävad lauda järgmiseks käiguks.',
    bottom: 'all', top: 'peal', onCart: 'kärul ✓', tapIt: 'vajuta, kui on kärul', stays: 'jääb lauda',
    count: (n: number, m: number) => `${n}/${m} kärul`, loaded: 'Laaditud — mine ✓',
    more: (aisle: string, n: number, pcs: number) => `Veel ${aisle} jaoks laua juures: ${n} kasti (${pcs} tk) — võta ka need, kui mahuvad`,
    takeToo: 'võta ka', taken: 'võetud ✓', plusMore: (n: number, m: number, x: number) => `${n}/${m} plaanitud + ${x} veel kärul`,
    pcs: (n: number) => `${n} tk`, box: 'Kast', stopOf: (i: number, n: number) => `Peatus ${i}/${n}`, done: 'Tehtud — riiulil ✓',
    noShelf: 'Sellel tootel pole veel riiulit. Vali vaba riiul ja kirjuta see siia:', shelfFull: 'Riiul täis — vii uuele riiulile',
    moveOn: 'Jätka uuele riiulile viimist', bringBack: 'Too tagasi lauda', typeLabel: 'kirjuta riiuli silt',
    toStock: 'Need suurused saavad selle riiuli (olid Ä-s)', allDone: 'Kõik kastid on riiulil ✓',
    backAt: (s: string) => `Vii tühjad kastid tagasi: ${s}.`, backBtn: 'Tagasi laua juures ✓',
    moreReady: (w: string, n: number) => `Sissetulek ${w}: veel ${n} kasti valmis`, nextTrip: 'Järgmine käik (PIN)',
    nothingReady: 'Praegu pole rohkem midagi valmis.', ok: 'OK', stops: 'Kõik peatused', shelved: 'riiulil',
    emptyTitle: 'Kõik on riiulitel ✓', emptyHint: 'Veendu, et viimane kast on tühi — sinna ei jäänud midagi.', emptyBtn: 'Viimane kast on tühi ✓',
    goBack: (s: string) => `Võta kõik kastid ja mine tagasi: ${s}.`, doneBtn: 'Tehtud ✓',
    shelfBack: 'Pane kastid riiulile tagasi.', newRound: 'Alusta uut käiku',
    newRoundOf: (w: string, n: number) => `Sissetulek ${w}: ${n} kasti valmis`,
    endSession: 'Lõpeta riiulisse panek', handover: 'Anna üle', handoverTitle: 'Järgmine inimene: sisesta oma PIN', cancel: 'Tühista',
    sessionOf: (name: string) => `Riiulisse paneb: ${name}`,
  },
  ru: {
    trip: (n: number, name: string) => `Рейс ${n} · ${name}`, inbound: (w: string) => `Поставка ${w}`, pause: 'Пауза',
    loadAt: (s: string) => `Загрузите тележку: ${s}`,
    loadHint: 'Ставьте коробки на тележку в этом порядке — первая вниз, последняя наверх (это ваша первая остановка). Нажимайте на каждую коробку, когда она на тележке; остальные остаются на станции до следующего рейса.',
    bottom: 'низ', top: 'верх', onCart: 'на тележке ✓', tapIt: 'нажмите, когда на тележке', stays: 'остаётся на станции',
    count: (n: number, m: number) => `${n} из ${m} на тележке`, loaded: 'Загружено — вперёд ✓',
    more: (aisle: string, n: number, pcs: number) => `Ещё для ${aisle} на станции: коробок ${n} (${pcs} шт) — возьмите и их, если помещаются`,
    takeToo: 'взять тоже', taken: 'взято ✓', plusMore: (n: number, m: number, x: number) => `${n} из ${m} запланированных + ещё ${x} на тележке`,
    pcs: (n: number) => `${n} шт`, box: 'Коробка', stopOf: (i: number, n: number) => `Остановка ${i} из ${n}`, done: 'Готово — на полке ✓',
    noShelf: 'У этого товара ещё нет полки. Выберите свободную полку и введите её:', shelfFull: 'Полка полная — на новую полку',
    moveOn: 'Продолжить перенос на новую полку', bringBack: 'Вернуть на станцию', typeLabel: 'введите метку полки',
    toStock: 'Эти размеры получают эту полку (были в Ä)', allDone: 'Все коробки на полках ✓',
    backAt: (s: string) => `Отвезите пустые коробки обратно: ${s}.`, backBtn: 'Вернулся на станцию ✓',
    moreReady: (w: string, n: number) => `Поставка ${w}: готово ещё коробок: ${n}`, nextTrip: 'Следующий рейс (PIN)',
    nothingReady: 'Сейчас больше ничего не готово.', ok: 'OK', stops: 'Все остановки', shelved: 'на полке',
    emptyTitle: 'Всё разложено ✓', emptyHint: 'Убедитесь, что последняя коробка пуста — в ней ничего не осталось.', emptyBtn: 'Последняя коробка пуста ✓',
    goBack: (s: string) => `Возьмите все коробки и вернитесь: ${s}.`, doneBtn: 'Готово ✓',
    shelfBack: 'Поставьте коробки обратно на полку.', newRound: 'Начать новый рейс',
    newRoundOf: (w: string, n: number) => `Поставка ${w}: готово коробок: ${n}`,
    endSession: 'Закончить раскладку', handover: 'Передать', handoverTitle: 'Следующий человек: введите свой PIN', cancel: 'Отмена',
    sessionOf: (name: string) => `Раскладывает: ${name}`,
  },
};
export const tripText = (lang?: string | null) => T[(lang as Lang)] || T.en;

const C = { card: 'bg-white border border-[#9aa0a6] rounded-2xl', muted: 'text-[#5f6368]' };

type AfterRound = { ready: ReadyInbound[]; session: SessionView | null; inbound: { id: number; wgr_order_id: string | null } };
export type SessionView = { id: number; person: string; name: string; lang: string; paused: boolean; why: string | null;
                            last_inbound: { id: number; wgr_order_id: string | null } | null };

export default function TabletTrip({ view, onView, onPaused, onClose }: {
  view: TripView;
  onView: (v: TripView) => void;
  /** Pause: the session (and the round) wait on this cart; the tablet goes back to its home screen */
  onPaused: () => void;
  /** End replenishing (or the round ended with nothing more wanted) */
  onClose: () => void;
}) {
  const tx = tripText(view.trip.lang);
  const { trip, stops } = view;
  const iid = view.inbound.id;
  const wgr = view.inbound.wgr_order_id || String(iid);
  const [err, setErr] = useState('');
  const [busy, setBusy] = useState(false);
  const [onCart, setOnCart] = useState<Set<number>>(new Set());     // the boxes TAPPED onto the cart (Fernando 1.11)
  const [shelf, setShelf] = useState('');
  const [label, setLabel] = useState<string | null>(null);        // a typed shelf label (the scan-confirm setting)
  const [moveId, setMoveId] = useState<number | null>(null);
  const [after, setAfter] = useState<AfterRound | null>(null);
  const [handover, setHandover] = useState(false);
  const lastErr = useRef('');                                     // the refusal of the call just made (state lags a render)

  const call = async (path: string, body: any = {}): Promise<any | null> => {
    setBusy(true); setErr('');
    try {
      const r = await post(path, body);
      lastErr.current = '';
      if (!r.ok) { const m = await detailOf(r); lastErr.current = m; setErr(m); scanSound('error'); return null; }
      return await r.json();
    } catch { setErr('No connection to the WMS'); return null; } finally { setBusy(false); }
  };
  const refresh = async () => {
    const r = await authFetch(`${API}/inbound/tablet/ready`);
    if (r.ok) { const j = await r.json(); if (j.my_trip) onView(j.my_trip); }
  };

  // ── after Done: "Put the boxes back on the shelf" + Start a new round (no PIN — the session goes on, 2.11) ──
  if (after) {
    return <BetweenRounds session={after.session} ready={after.ready} lastInbound={after.inbound} shelfHint
      onTrip={v => { setAfter(null); onView(v); }} onPaused={onPaused} onEnded={onClose} />;
  }
  if (handover) {
    return <HandoverPad lang={view.trip.lang} onCancel={() => setHandover(false)}
      onDone={j => { setHandover(false); if (j.trip_view) onView(j.trip_view); }} />;
  }

  // ── the move to a new shelf, inside the trip ──
  if (moveId) {
    return (
      <div className="fixed inset-0 bg-[#e5e7eb] overflow-y-auto">
        <MoveView id={String(iid)} mid={String(moveId)} onDone={() => { setMoveId(null); refresh(); }} />
      </div>
    );
  }

  const header = (
    <span className="flex gap-2">
      <button onClick={() => setHandover(true)} className={`${C.card} px-5 py-2 text-lg`}>{tx.handover}</button>
      <button onClick={async () => { const j = await call(`/tablet/trip/${trip.id}/pause`); if (j) onPaused(); }}
        className={`${C.card} px-5 py-2 text-lg`}>{tx.pause}</button>
    </span>
  );

  // ── loading at the station ──
  if (trip.phase === 'loading') {
    const order = [...stops].reverse();                          // the first loaded = the last stop = bottom
    return (
      <Shell title={tx.inbound(wgr)} sub={tx.trip(trip.trip_no, trip.name)} right={header}>
        <div className="text-3xl font-black mb-1">{tx.loadAt(view.station)}</div>
        <div className={`text-lg ${C.muted} mb-4`}>{tx.loadHint}</div>
        <div className="space-y-2">
          {order.map((s_, k) => {
            const on = onCart.has(s_.box_id);
            return (
              <button key={s_.box_id} onClick={() => setOnCart(p => { const n = new Set(p); if (n.has(s_.box_id)) n.delete(s_.box_id); else n.add(s_.box_id); return n; })}
                className={`w-full text-left p-4 flex items-center gap-4 rounded-2xl border-2 ${on ? 'bg-green-700 border-green-800 text-white' : 'bg-white border-[#9aa0a6]'}`}>
                <div className={`text-sm w-14 ${on ? 'text-white/80' : C.muted}`}>{k === 0 ? tx.bottom : k === order.length - 1 ? tx.top : ''}</div>
                <div className="text-5xl font-black w-28">{s_.label}{(s_.boxes || 1) > 1 ? <span className="text-2xl"> ×{s_.boxes}</span> : null}</div>
                <div className={`flex-1 text-lg ${on ? 'text-white/90' : C.muted}`}>{tx.pcs(s_.pieces)}</div>
                <div className="text-xl font-bold">{on ? tx.onCart : <span className={`text-base font-normal ${C.muted}`}>{tx.tapIt}</span>}</div>
              </button>
            );
          })}
        </div>
        {(view.more_in_aisle || []).length > 0 && (
          <div className="mt-5">
            <div className="text-xl font-bold mb-2">{tx.more(trip.aisle, view.more_in_aisle!.length, view.more_in_aisle!.reduce((n, m) => n + (m.pieces || 0), 0))}</div>
            <div className="space-y-2">
              {view.more_in_aisle!.map(m => {
                const on = onCart.has(m.box_id);
                return (
                  <button key={m.box_id} onClick={() => setOnCart(p => { const n = new Set(p); if (n.has(m.box_id)) n.delete(m.box_id); else n.add(m.box_id); return n; })}
                    className={`w-full text-left p-3 flex items-center gap-4 rounded-2xl border-2 border-dashed ${on ? 'bg-green-700 border-green-800 text-white' : 'bg-white border-[#9aa0a6]'}`}>
                    <div className="text-4xl font-black w-28">{m.label}{(m.boxes || 1) > 1 ? <span className="text-xl"> ×{m.boxes}</span> : null}</div>
                    <div className={`flex-1 text-lg ${on ? 'text-white/90' : C.muted}`}>{m.no_location ? '?' : m.section} · {tx.pcs(m.pieces || 0)}</div>
                    <div className="text-lg font-bold">{on ? tx.taken : <span className={`text-base font-normal ${C.muted}`}>{tx.takeToo}</span>}</div>
                  </button>
                );
              })}
            </div>
          </div>
        )}
        {(() => {
          const plannedOn = stops.filter(x => onCart.has(x.box_id)).length;
          const extraOn = onCart.size - plannedOn;
          return (
            <div className="mt-4 text-2xl font-bold">{extraOn > 0 ? tx.plusMore(plannedOn, stops.length, extraOn) : tx.count(plannedOn, stops.length)}
              {plannedOn < stops.length && onCart.size > 0 && <span className={`text-lg font-normal ${C.muted}`}> · {stops.length - plannedOn} {tx.stays}</span>}</div>
          );
        })()}
        {err && <div className="text-red-700 text-lg font-semibold mt-3">{err}</div>}
        <button disabled={busy || onCart.size === 0} onClick={async () => {
          const j = await call(`/tablet/trip/${trip.id}/loaded`, { loaded_ids: Array.from(onCart) });
          if (j) { setOnCart(new Set()); if (j.trip.phase === 'done') onClose(); else onView(j); }
        }} className="mt-4 w-full py-5 rounded-2xl bg-[#111111] text-white text-2xl font-bold disabled:opacity-40">{tx.loaded}</button>
      </Shell>
    );
  }

  // ── every stop done (Fernando 2.11): a) "All replenished — make sure the last box is empty" → b) "Pick up all the
  // boxes and go back to the station" + Done ──
  if (view.all_done || view.next_stop === null) {
    const checked = !!trip.empty_checked;
    return (
      <Shell title={tx.inbound(wgr)} sub={tx.trip(trip.trip_no, trip.name)} right={header}>
        <div className="text-center py-12 space-y-6">
          {!checked ? (
            <>
              <div className="text-5xl font-black">{tx.emptyTitle}</div>
              <div className="text-2xl">{tx.emptyHint}</div>
              {err && <div className="text-red-700 text-lg font-semibold">{err}</div>}
              <button disabled={busy} onClick={async () => { const j = await call(`/tablet/trip/${trip.id}/empty-checked`); if (j) { scanSound('ok'); onView(j); } }}
                className="px-10 py-5 rounded-2xl bg-green-700 text-white text-2xl font-bold">{tx.emptyBtn}</button>
            </>
          ) : (
            <>
              <div className="text-4xl font-black">{tx.goBack(view.station)}</div>
              {err && <div className="text-red-700 text-lg font-semibold">{err}</div>}
              <button disabled={busy} onClick={async () => {
                const j = await call(`/tablet/trip/${trip.id}/back`);
                if (j) { scanSound('ok'); setAfter({ ready: j.ready || [], session: j.session || null, inbound: j.inbound || view.inbound }); }
              }} className="px-14 py-6 rounded-2xl bg-[#111111] text-white text-3xl font-bold">{tx.doneBtn}</button>
            </>
          )}
        </div>
      </Shell>
    );
  }

  // ── walking: one stop at a time ──
  const k = view.next_stop;
  const s_: TabletStop = stops[k];
  const doneBody = (extra: any = {}) => ({ box_id: s_.box_id, confirm: 'tap', ...extra });
  const tapDone = async () => {
    const body = s_.no_location ? doneBody({ code: shelf.trim() }) : label !== null ? doneBody({ confirm: 'scan', code: label.trim() }) : doneBody();
    const j = await call(`/${iid}/trip/${trip.id}/stop-done`, body);
    if (j) { scanSound('ok'); setShelf(''); setLabel(null); onView(j); }
    else if (/Scan the/.test(lastErr.current) && label === null) setLabel('');
  };
  return (
    <Shell title={tx.inbound(wgr)} sub={`${tx.trip(trip.trip_no, trip.name)} · ${tx.stopOf(k + 1, stops.length)}`} right={header}>
      <div className={`${C.card} p-6 text-center`}>
        <div className="text-[clamp(3rem,12vw,7rem)] font-black leading-none">{s_.special ? s_.section : (s_.no_location ? '?' : s_.section)}</div>
        <div className="mt-3 text-2xl"><span className={C.muted}>{tx.box}</span> <b className="text-4xl">{s_.label}</b>{(s_.boxes || 1) > 1 ? ` ×${s_.boxes}` : ''} · {tx.pcs(s_.pieces)}</div>
        {s_.to_stock && <div className="mt-2 text-lg text-amber-800 font-semibold">{tx.toStock}</div>}
        <div className={`mt-3 text-lg ${C.muted}`}>{s_.items.map(it => `${it.size || it.article_number} ×${it.pieces}`).join(' · ')}</div>
        {s_.items[0]?.name && <div className={`text-base ${C.muted} truncate`}>{s_.items[0].name}</div>}
      </div>
      {s_.no_location && (
        <div className="mt-4">
          <div className="text-xl mb-2">{tx.noShelf}</div>
          <input value={shelf} onChange={e => setShelf(e.target.value.toUpperCase())} placeholder="K 45"
            className="w-48 text-3xl px-3 py-2 rounded-xl border border-[#9aa0a6] bg-white" />
        </div>
      )}
      {label !== null && !s_.no_location && (
        <div className="mt-4 flex items-center gap-3">
          <span className="text-lg">{tx.typeLabel}:</span>
          <input autoFocus value={label} onChange={e => setLabel(e.target.value.toUpperCase())} placeholder={s_.section}
            className="w-48 text-3xl px-3 py-2 rounded-xl border border-[#9aa0a6] bg-white" />
        </div>
      )}
      {err && <div className="text-red-700 text-lg font-semibold mt-3">{err}</div>}
      <button disabled={busy || (s_.no_location && !shelf.trim())} onClick={tapDone}
        className="mt-5 w-full py-6 rounded-2xl bg-green-700 text-white text-3xl font-bold disabled:opacity-40">{tx.done}</button>
      <div className="mt-3 flex flex-wrap gap-2">
        {!s_.special && !s_.no_location && (
          <button disabled={busy} onClick={async () => {
            if (s_.move) { setMoveId(s_.move.id); return; }
            const j = await call(`/${iid}/move/start`, { box_id: s_.box_id });
            if (j?.move?.id) setMoveId(j.move.id);
          }} className={`${C.card} px-4 py-3 text-lg`}>{s_.move ? tx.moveOn : tx.shelfFull}</button>
        )}
        <button disabled={busy} onClick={async () => { const j = await call(`/${iid}/trip/${trip.id}/leave`, { box_id: s_.box_id }); if (j) { if (j.trip.phase === 'done') onClose(); else onView(j); } }}
          className={`${C.card} px-4 py-3 text-lg`}>{tx.bringBack}</button>
        {label === null && !s_.no_location && <button onClick={() => setLabel('')} className={`px-3 py-3 text-base underline ${C.muted}`}>{tx.typeLabel}</button>}
      </div>
      <div className="mt-6">
        <div className={`text-sm uppercase tracking-wider ${C.muted} mb-1`}>{tx.stops}</div>
        {stops.map((x, i) => (
          <div key={x.box_id} className={`flex gap-3 text-lg ${i === k ? 'font-bold' : x.done ? `${C.muted} line-through` : ''}`}>
            <span className="w-16">{x.label}</span><span>{x.special ? x.section : x.no_location ? '?' : x.section}</span>
            {x.done && <span className="no-underline">✓ {x.to ? `${tx.shelved} ${x.to}` : ''}</span>}
          </div>
        ))}
      </div>
    </Shell>
  );
}

/** Between rounds (Fernando 2.11): "Put the boxes back on the shelf" and Start a new round — no PIN, the session goes on;
 *  the other inbounds with ready boxes; End replenishing; Pause and Hand over. Also the tablet's home screen while a
 *  session is open between rounds. */
export function BetweenRounds({ session, ready, lastInbound, shelfHint, onTrip, onPaused, onEnded }: {
  session: SessionView | null; ready: ReadyInbound[]; lastInbound: { id: number; wgr_order_id: string | null } | null;
  shelfHint?: boolean; onTrip: (v: TripView) => void; onPaused: () => void; onEnded: () => void;
}) {
  const tx = tripText(session?.lang);
  const [name, setName] = useState(session?.name || session?.person || '');
  const [err, setErr] = useState('');
  const [busy, setBusy] = useState(false);
  const [handover, setHandover] = useState(false);
  const here = lastInbound ? ready.find(r => r.inbound_id === lastInbound.id) : undefined;
  const others = ready.filter(r => !here || r.inbound_id !== here.inbound_id);
  const call = async (path: string, body: any = {}) => {
    setBusy(true); setErr('');
    try {
      const r = await post(path, body);
      if (r.status === 401) { onPaused(); return null; }               // paused (idle) — the home screen asks the PIN
      if (!r.ok) { setErr(await detailOf(r)); scanSound('error'); return null; }
      return await r.json();
    } catch { setErr('No connection to the WMS'); return null; } finally { setBusy(false); }
  };
  const start = async (inboundId: number) => { const j = await call('/tablet/next', { inbound_id: inboundId }); if (j) onTrip(j); };
  if (handover) {
    return <HandoverPad lang={session?.lang} onCancel={() => setHandover(false)}
      onDone={j => { setHandover(false); setName(j.session?.name || j.session?.person || name); }} />;
  }
  const head = (
    <span className="flex gap-2">
      <button onClick={() => setHandover(true)} className={`${C.card} px-5 py-2 text-lg`}>{tx.handover}</button>
      <button onClick={async () => { const j = await call('/tablet/pause'); if (j) onPaused(); }} className={`${C.card} px-5 py-2 text-lg`}>{tx.pause}</button>
    </span>
  );
  return (
    <Shell title={tx.sessionOf(name)} right={head}>
      <div className="text-center py-8 space-y-6">
        {shelfHint && <div className="text-4xl font-black">{tx.shelfBack}</div>}
        {here ? (
          <div className="space-y-2">
            <div className="text-2xl">{tx.newRoundOf(here.wgr_order_id || String(here.inbound_id), here.boxes)}</div>
            <button disabled={busy} onClick={() => start(here.inbound_id)} className="px-14 py-6 rounded-2xl bg-[#111111] text-white text-3xl font-bold">{tx.newRound}</button>
          </div>
        ) : others.length === 0 && <div className={`text-2xl ${C.muted}`}>{tx.nothingReady}</div>}
        {others.map(r => (
          <button key={r.inbound_id} disabled={busy} onClick={() => start(r.inbound_id)} className={`${C.card} w-full text-left p-4`}>
            <div className="text-2xl font-black">{tx.newRoundOf(r.wgr_order_id || String(r.inbound_id), r.boxes)}</div>
            <div className={`text-lg ${C.muted}`}>{r.supplier_name} · {tx.newRound} →</div>
          </button>
        ))}
        {err && <div className="text-red-700 text-lg font-semibold">{err}</div>}
        <div className="pt-4">
          <button disabled={busy} onClick={async () => { const j = await call('/tablet/end'); if (j) onEnded(); }}
            className={`${C.card} px-8 py-3 text-lg`}>{tx.endSession}</button>
        </div>
      </div>
    </Shell>
  );
}

/** Hand over (Fernando 2.11): the NEXT person enters their PIN — the session and the round on the cart are theirs. */
function HandoverPad({ lang, onDone, onCancel }: { lang?: string | null; onDone: (j: any) => void; onCancel: () => void }) {
  const tx = tripText(lang);
  const [err, setErr] = useState('');
  const [busy, setBusy] = useState(false);
  return (
    <div className="fixed inset-0 z-50 bg-[#e5e7eb] text-[#111111] overflow-y-auto flex items-start justify-center px-5 py-8">
      <PinPad title={tx.handoverTitle} error={err} busy={busy} cancelLabel={tx.cancel} onCancel={onCancel} onSubmit={async pin => {
        setBusy(true); setErr('');
        try {
          const r = await post('/tablet/handover', { pin });
          if (!r.ok) { setErr(await detailOf(r)); return; }
          onDone(await r.json());
        } catch { setErr('No connection to the WMS'); } finally { setBusy(false); }
      }} />
    </div>
  );
}

function Shell({ title, sub, right, children }: { title: string; sub?: string; right?: React.ReactNode; children: React.ReactNode }) {
  return (
    <div className="fixed inset-0 bg-[#e5e7eb] text-[#111111] flex flex-col overflow-y-auto">
      <div className="flex-none border-b border-[#c8ccd2] bg-[#d9dce1] px-5 py-3 flex items-center gap-3">
        <span className="font-bold text-lg">{title}</span>
        {sub && <span className="text-sm text-[#5f6368]">{sub}</span>}
        <span className="ml-auto">{right}</span>
      </div>
      <div className="flex-1 px-5 py-6 max-w-3xl w-full mx-auto">{children}</div>
    </div>
  );
}
